<template>
  <PrintPageLandscape>
    <PresentationPage :page-number="pageNumber" :page-count="pageCount">
      <p class="presentation-page__eyebrow">Anlage &amp; Finanzierung</p>
      <h2 class="presentation-page__title">Musterberechnung — Projekteckdaten</h2>
      <p class="presentation-page__text">
        {{ installationType }} {{ overallInstallationPower }} kWp ·
        {{ initialFeedInTariffDurationYears }} Jahre EEG · Investor-Profil {{ taxTable }} · zvE
        {{ taxableIncomeEur }}.
      </p>
      <div class="presentation-page__spec-columns">
        <article class="presentation-page__spec-panel">
          <div class="presentation-page__spec-panel-header">Anlagedaten</div>
          <div
            v-for="row in plantDataRows"
            :key="row.label"
            class="presentation-page__spec-panel-row"
          >
            <span class="presentation-page__spec-panel-label">{{ row.label }}</span>
            <span class="presentation-page__spec-panel-value">{{ row.value }}</span>
          </div>
        </article>

        <article class="presentation-page__spec-panel">
          <div
            class="presentation-page__spec-panel-header presentation-page__spec-panel-header--brand-secondary"
          >
            Investition &amp; Finanzierung
          </div>
          <div
            v-for="row in financingRows"
            :key="row.label"
            class="presentation-page__spec-panel-row"
          >
            <span class="presentation-page__spec-panel-label">{{ row.label }}</span>
            <span class="presentation-page__spec-panel-value">{{ row.value }}</span>
          </div>
        </article>

        <div class="presentation-page__summary-panel">
          <p class="presentation-page__summary-panel-eyebrow">Auf einen Blick</p>
          <p class="presentation-page__summary-panel-value">{{ overallInstallationPower }} kWp</p>
          <p class="presentation-page__summary-panel-text">
            {{ installationType }} · {{ initialFeedInTariffDurationYears }} Jahre EEG
          </p>

          <div class="presentation-page__summary-panel-accent" aria-hidden="true"></div>

          <p class="presentation-page__summary-panel-text">Investition gesamt</p>
          <p class="presentation-page__summary-panel-value">{{ totalInvestment }}</p>

          <div class="presentation-page__summary-panel-accent" aria-hidden="true"></div>

          <p class="presentation-page__summary-panel-eyebrow">§ 7g IAB-Wirkung</p>
          <p class="presentation-page__summary-panel-value">{{ iab50PercentOfInvestment }}</p>
          <p class="presentation-page__summary-panel-text">
            IAB-Volumen · bis zu {{ iabTaxRefundYear0 }} Steuererstattung bei
            {{ effectiveTaxRatePercent }}
          </p>
        </div>
      </div>

      <p class="presentation-page__disclaimer">
        Musterberechnung ohne Haftung auf Richtigkeit. Keine Steuer- oder Rechtsberatung.
      </p>
    </PresentationPage>
  </PrintPageLandscape>
</template>

<script>
import PrintPageLandscape from '@/components/Print/PrintPageLandscape.vue';
import PresentationPage from '@/views/MasterPresentation/PresentationPage';
import { formatNumber, NUMBER_FORMAT, parseFiniteNumber } from '@/helpers/numberFormat';

const EQUITY_SHARE_PERCENT = 30;
const LOAN_SHARE_PERCENT = 70;

export default {
  name: 'MasterPresentationPrintPage37',
  components: { PrintPageLandscape, PresentationPage },
  props: {
    project: { type: Object, default: null },
    pageCount: { type: Number, default: null },
    pageNumber: { type: Number, default: 0 },
  },
  computed: {
    iab() {
      return this.project?.investmentDeductionAllowance ?? {};
    },

    parameterKeyResults() {
      return this.project?.finModel?.parameterKeyResults ?? {};
    },

    totalInvestmentAmount() {
      return parseFiniteNumber(this.project?.effectiveSettlementValue);
    },

    overallInstallationPower() {
      return formatNumber(
        this.project?.solarPlant?.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL_TRIPLE
      );
    },

    installationType() {
      return this.iab.installationType || 'Dachfläche';
    },

    specificAnnualYieldKwhPerKwp() {
      return formatNumber(this.iab.specificAnnualYieldKwhPerKwp, NUMBER_FORMAT.DECIMAL);
    },

    initialFeedInTariffDurationYears() {
      return formatNumber(this.iab.initialFeedInTariffDurationYears, NUMBER_FORMAT.DECIMAL_ZERO);
    },

    feedInTariffGuaranteeCaseEurPerKwh() {
      return formatNumber(
        this.iab.feedInTariffGuaranteeCaseEurPerKwh,
        NUMBER_FORMAT.DECIMAL_QUADRUPLE
      );
    },

    feedInTariffMedianCaseEurPerKwh() {
      return formatNumber(this.iab.feedInTariffMedianCaseEurPerKwh, NUMBER_FORMAT.DECIMAL);
    },

    feedInTariffOptimalCaseEurPerKwh() {
      return formatNumber(this.iab.feedInTariffOptimalCaseEurPerKwh, NUMBER_FORMAT.DECIMAL);
    },

    degradationPercentPerYear() {
      return formatNumber(this.iab.degradationPercentPerYear, NUMBER_FORMAT.DECIMAL);
    },

    annualOperatingCostTotal() {
      const costPerKwp = parseFiniteNumber(this.iab.annualOperatingCostPerKwpEur);
      const powerKwp = parseFiniteNumber(this.project?.solarPlant?.solarPlantOverallPower);
      if (costPerKwp === null || powerKwp === null) {
        return formatNumber(null, NUMBER_FORMAT.CURRENCY);
      }
      return formatNumber(costPerKwp * powerKwp, NUMBER_FORMAT.CURRENCY);
    },

    annualOperatingCostPerKwpEur() {
      return formatNumber(this.iab.annualOperatingCostPerKwpEur, NUMBER_FORMAT.CURRENCY);
    },

    totalInvestment() {
      return formatNumber(this.totalInvestmentAmount, NUMBER_FORMAT.CURRENCY);
    },

    equityAmount() {
      if (this.totalInvestmentAmount === null) {
        return formatNumber(null, NUMBER_FORMAT.CURRENCY);
      }
      return formatNumber(
        this.totalInvestmentAmount * (EQUITY_SHARE_PERCENT / 100),
        NUMBER_FORMAT.CURRENCY
      );
    },

    loanAmount() {
      if (this.totalInvestmentAmount === null) {
        return formatNumber(null, NUMBER_FORMAT.CURRENCY);
      }
      return formatNumber(
        this.totalInvestmentAmount * (LOAN_SHARE_PERCENT / 100),
        NUMBER_FORMAT.CURRENCY
      );
    },

    durationYears() {
      return formatNumber(this.iab.durationYears, NUMBER_FORMAT.DECIMAL_ZERO);
    },

    interestRatePercent() {
      return formatNumber(this.iab.interestRatePercent, NUMBER_FORMAT.DECIMAL);
    },

    repaymentRatePercent() {
      return formatNumber(this.iab.repaymentRatePercent, NUMBER_FORMAT.DECIMAL);
    },

    fixedRateYears() {
      return formatNumber(this.iab.fixedRateYears, NUMBER_FORMAT.DECIMAL_ZERO);
    },

    effectiveTaxRatePercent() {
      return formatNumber(this.iab.effectiveTaxRatePercent, NUMBER_FORMAT.DECIMAL);
    },

    taxTable() {
      return this.iab.taxTable || 'Splitting';
    },

    taxableIncomeEur() {
      return formatNumber(this.iab.taxableIncomeEur, NUMBER_FORMAT.CURRENCY);
    },

    iabPercent() {
      return formatNumber(this.iab.iabPercent, NUMBER_FORMAT.DECIMAL);
    },

    iab50PercentOfInvestment() {
      return formatNumber(
        parseFiniteNumber(this.parameterKeyResults.iab50PercentOfInvestment),
        NUMBER_FORMAT.CURRENCY
      );
    },

    iabTaxRefundYear0() {
      return formatNumber(
        parseFiniteNumber(this.parameterKeyResults.iabTaxRefundYear0),
        NUMBER_FORMAT.CURRENCY
      );
    },

    plantDataRows() {
      return [
        { label: 'Anlagentyp', value: this.installationType },
        { label: 'Anlagengröße', value: `${this.overallInstallationPower} kWp` },
        { label: 'Spez. Jahresertrag', value: `${this.specificAnnualYieldKwhPerKwp} kWh/kWp` },
        {
          label: 'Laufzeit Erstvergütung',
          value: `${this.initialFeedInTariffDurationYears} Jahre`,
        },
        { label: 'EEG Garantiefall', value: `${this.feedInTariffGuaranteeCaseEurPerKwh} €/kWh` },
        { label: 'EEG Medianfall', value: `${this.feedInTariffMedianCaseEurPerKwh} €/kWh` },
        { label: 'EEG Optimalfall', value: `${this.feedInTariffOptimalCaseEurPerKwh} €/kWh` },
        { label: 'Degradation p.a.', value: `${this.degradationPercentPerYear} %` },
        {
          label: 'Betriebskosten p.a.',
          value: `${this.annualOperatingCostTotal} / ${this.annualOperatingCostPerKwpEur}/kWp`,
        },
      ];
    },

    financingRows() {
      return [
        { label: 'Investition gesamt', value: this.totalInvestment },
        {
          label: `Eigenkapital (${EQUITY_SHARE_PERCENT} %)`,
          value: this.equityAmount,
        },
        {
          label: `Darlehen (${LOAN_SHARE_PERCENT} %)`,
          value: this.loanAmount,
        },
        {
          label: 'Laufzeit / Zinssatz',
          value: `${this.durationYears} J. / ${this.interestRatePercent} %`,
        },
        {
          label: 'Tilgungssatz',
          value: `${this.repaymentRatePercent} % (FS ${this.fixedRateYears} J.)`,
        },
        { label: 'Effektiver Steuersatz', value: `${this.effectiveTaxRatePercent} %` },
        { label: 'Steuertabelle', value: this.taxTable },
        { label: 'zvE 2023', value: this.taxableIncomeEur },
        { label: `IAB (${this.iabPercent} % KP)`, value: this.iab50PercentOfInvestment },
      ];
    },
  },
};
</script>
